import React from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import styled from 'styled-components';

const RadioInput = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
`;

// Circle control (hidden for buttonStyle variant)
type RadioControlProps = {
  $buttonStyle?: boolean;
};

type RadioLabelProps = {
  $buttonStyle?: boolean;
  $checked?: boolean;
  $color?: string;
  $selectedColor?: string;
  $selectedBgColor?: string;
};

type RadioProps = Omit<ComponentPropsWithoutRef<'input'>, 'type'> & {
  label?: ReactNode;
  buttonStyle?: boolean;
  color?: string;
  selectedColor?: string;
  selectedBgColor?: string;
  fullWidth?: boolean;
};

const RadioControl = styled.span<RadioControlProps>`
  position: relative;
  display: ${({ $buttonStyle }) => ($buttonStyle ? 'none' : 'inline-block')};
  width: 18px;
  height: 18px;
  margin-inline-end: 8px;
  border: 2px solid #666;
  border-radius: 50%;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    inset-inline-start: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.primary || '#007bff'};
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  ${RadioInput}:checked + & {
    border-color: ${({ theme }) => theme.primary || '#007bff'};
    &::after {
      opacity: 1;
    }
  }

  // Default focus style (non buttonStyle)
  ${RadioInput}:focus-visible + & {
    outline: var(--focus-outline);
    outline-offset: 2px;
    border-radius: 50%;
  }

  ${RadioInput}:focus + & {
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
  }
`;

const RadioLabel = styled.label<RadioLabelProps>`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  font-size: 14px;
  line-height: 1.2;
  ${({ $color }) => $color && `color: ${$color};`}

  /* Button-style variant */
  ${({ $buttonStyle }) =>
    $buttonStyle &&
    `
    border: 1px solid #565656;
    padding: 8px 16px;
    border-radius: 6px;
    background: #ffffff;
    font-weight: 500;
    justify-content: center;
    /* Remove min-width to allow perfect flush flex sizing */
    min-width: 0;
    box-sizing: border-box;
    width: 100%;
    transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  `}

  ${({ $buttonStyle, $checked, $selectedBgColor, $selectedColor, theme }) =>
    $buttonStyle &&
    $checked &&
    `
    background: ${$selectedBgColor || $selectedColor || theme.primary || '#007bff'};
    color: #ffffff;
    border-color: ${$selectedBgColor || $selectedColor || theme.primary || '#007bff'};
  `}

  svg {
    transition:
      color 0.2s ease,
      fill 0.2s ease;
    margin-inline-end: 6px;
    ${({ $checked, $selectedColor, $buttonStyle }) =>
      $checked &&
      $selectedColor &&
      `
      color: ${$buttonStyle ? '#ffffff' : $selectedColor};
      fill: ${$buttonStyle ? '#ffffff' : $selectedColor};
    `}
  }

  /* Focus style for buttonStyle using keyboard navigation only */
  ${({ $buttonStyle }) =>
    $buttonStyle &&
    `
    &:has(input[type="radio"]:focus-visible) {
      outline: var(--focus-outline);
      outline-offset: 2px;
    }
  `}
`;

const Radio = ({
  id,
  name,
  value,
  checked,
  onChange,
  label,
  buttonStyle,
  color,
  selectedColor,
  selectedBgColor,
  fullWidth,
  ...props
}: RadioProps) => {
  return (
    <RadioLabel
      $checked={checked}
      $buttonStyle={buttonStyle}
      $color={color}
      $selectedColor={selectedColor}
      $selectedBgColor={selectedBgColor}
      style={fullWidth ? { flex: 1 } : undefined}
    >
      <RadioInput type="radio" id={id} name={name} value={value} checked={checked} onChange={onChange} {...props} />
      <RadioControl $buttonStyle={buttonStyle} />
      {label}
    </RadioLabel>
  );
};

export default Radio;
